import { useState } from 'react';
import { BarChart3, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export interface PollOption {
  id: string;
  text: string;
  voters: string[];
}

export interface PollData {
  question: string;
  options: PollOption[];
  allowMultiple?: boolean;
  isAnonymous?: boolean;
  closedAt?: string;
}

interface PollBubbleProps {
  data: PollData;
  isOwn: boolean;
  currentUserId?: string;
  onVote?: (optionId: string) => void;
}

export function PollBubble({ data, isOwn, currentUserId, onVote }: PollBubbleProps) {
  const totalVotes = data.options.reduce((sum, o) => sum + o.voters.length, 0);
  const hasVoted = currentUserId ? data.options.some(o => o.voters.includes(currentUserId)) : false;
  const isClosed = data.closedAt ? new Date(data.closedAt) < new Date() : false;
  const showResults = hasVoted || isClosed;

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className={cn(
          "rounded-2xl overflow-hidden max-w-[300px] w-full",
          isOwn
            ? "bg-[hsl(200,80%,40%)] text-white"
            : "bg-[#F0F0F0] dark:bg-[#2A2A2A] text-foreground"
        )}
      >
        {/* Header */}
        <div className={cn(
          "px-4 py-2.5 flex items-center gap-2",
          isOwn ? "bg-white/10" : "bg-primary/10"
        )}>
          <BarChart3 className="h-4 w-4 shrink-0" />
          <span className="text-xs font-bold uppercase tracking-wider">Poll</span>
          {data.isAnonymous && <span className="text-[10px] opacity-60 ml-auto">Anonymous</span>}
        </div>

        <div className="px-4 py-3 space-y-2.5">
          <h4 className="text-base font-bold leading-tight">{data.question}</h4>

          <div className="space-y-1.5">
            {data.options.map((option) => {
              const pct = totalVotes > 0 ? Math.round((option.voters.length / totalVotes) * 100) : 0;
              const isMyVote = currentUserId ? option.voters.includes(currentUserId) : false;

              return (
                <button
                  key={option.id}
                  onClick={() => !isClosed && onVote?.(option.id)}
                  disabled={isClosed}
                  className={cn(
                    "w-full relative rounded-xl overflow-hidden text-left transition-all",
                    !showResults && !isClosed
                      ? isOwn ? "bg-white/10 hover:bg-white/20 active:bg-white/25" : "bg-muted hover:bg-muted/80 active:bg-muted/60"
                      : "cursor-default"
                  )}
                >
                  {/* Result bar */}
                  {showResults && (
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                      className={cn(
                        "absolute inset-y-0 left-0 rounded-xl",
                        isMyVote
                          ? isOwn ? "bg-white/25" : "bg-primary/20"
                          : isOwn ? "bg-white/10" : "bg-muted-foreground/10"
                      )}
                    />
                  )}

                  <div className="relative px-3 py-2 flex items-center gap-2">
                    {isMyVote && showResults && (
                      <Check className="h-3.5 w-3.5 shrink-0" />
                    )}
                    <span className="text-sm font-medium flex-1 truncate">{option.text}</span>
                    {showResults && (
                      <span className="text-xs font-bold opacity-70 shrink-0">{pct}%</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs opacity-50 pt-0.5">
            <span>{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</span>
            {isClosed && <span>Poll closed</span>}
            {data.allowMultiple && !isClosed && <span>Multiple choice</span>}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
